// tslint:disable:no-namespace

import { Field } from '../shared/interfaces/hubspot-form-data';
import * as _ from 'lodash';
import * as fp from 'lodash/fp';

export namespace FormsUtils {
    export function trim(s: string): string {
        return s.trim ? s.trim() : s ? s.replace(/^\s+|\s+$/g, '') : s;
    }

    export function isArray(e) {
        return '[object Array]' === Object.prototype.toString.call(e);
    }

    export function isNumeric(value: any): boolean {
        return !this.isArray(value) && value - parseFloat(value) + 1 >= 0;
    }

    export function isObject(value: any): boolean {
        return value === Object(value);
    }

    export function isInteger(value: any): boolean {
        return 'number' === typeof value && isFinite(value) && Math.floor(value) === value;
    }

    export function isCheckboxes(field: Field): boolean {
        return field && field.fieldType === 'checkbox';
    }

    export function flattenValuesFromCheckboxList(checkboxes: any): string {
        const flattenedValues = _.flow([fp.filter((checkbox: any) => checkbox.checked), fp.flatMap((checkbox: any) => checkbox.value)])(
            checkboxes
        );
        return flattenedValues.join(',');
    }

    export function notEmpty(value: any, isRemoveSpace: boolean = false): boolean {
        return !isRemoveSpace
            ? value === 0 || (value !== undefined && value !== null && value !== '')
            : value === 0 || (value !== undefined && value !== null && String(value).trim() !== '');
    }

    export function getNearestLocale(): string {
        const navigatorLanguageInfo = FormsUtils.tryToGetNavigatorLanguageInfo();
        let locale = 'US';
        const lang = FormsUtils.getNearestLanguage();
        const codeByLanguage = FormsUtils.getNearestCountryCodeByLanguage();
        if (navigatorLanguageInfo.split('-')[1]) {
            locale = navigatorLanguageInfo.split('-')[1];
        } else if (codeByLanguage[lang]) {
            locale = codeByLanguage[lang];
        }
        return locale;
    }

    export function getNearestLanguage(): string {
        const navigatorLanguageInfo = FormsUtils.tryToGetNavigatorLanguageInfo();
        return navigatorLanguageInfo.split('-')[0] || 'en';
    }

    export function tryToGetNavigatorLanguageInfo(): string {
        if (navigator.languages !== undefined) {
            return navigator.languages[0];
        }
        return navigator.language;
    }

    export function getCountriesByCode() {
        return {
            AF: 'Afghanistan (‫افغانستان‬‎)',
            AL: 'Albania (Shqipëri)',
            DZ: 'Algeria (‫الجزائر‬‎)',
            AS: 'American Samoa',
            AD: 'Andorra',
            AO: 'Angola',
            AI: 'Anguilla',
            AG: 'Antigua and Barbuda',
            AR: 'Argentina',
            AM: 'Armenia (Հայաստան)',
            AW: 'Aruba',
            AU: 'Australia',
            AT: 'Austria (Österreich)',
            AZ: 'Azerbaijan (Azərbaycan)',
            BS: 'Bahamas',
            BH: 'Bahrain (‫البحرين‬‎)',
            BD: 'Bangladesh (বাংলাদেশ)',
            BB: 'Barbados',
            BY: 'Belarus (Беларусь)',
            BE: 'Belgium (België)',
            BZ: 'Belize',
            BJ: 'Benin (Bénin)',
            BM: 'Bermuda',
            BT: 'Bhutan (འབྲུག)',
            BO: 'Bolivia',
            BA: 'Bosnia and Herzegovina (Босна и Херцеговина)',
            BW: 'Botswana',
            BR: 'Brazil (Brasil)',
            IO: 'British Indian Ocean Territory',
            VG: 'British Virgin Islands',
            BN: 'Brunei',
            BG: 'Bulgaria (България)',
            BF: 'Burkina Faso',
            BI: 'Burundi (Uburundi)',
            KH: 'Cambodia (កម្ពុជា)',
            CM: 'Cameroon (Cameroun)',
            CA: 'Canada',
            CV: 'Cape Verde (Kabu Verdi)',
            BQ: 'Caribbean Netherlands',
            KY: 'Cayman Islands',
            CF: 'Central African Republic (République centrafricaine)',
            TD: 'Chad (Tchad)',
            CL: 'Chile',
            CN: 'China (中国)',
            CO: 'Colombia',
            KM: 'Comoros (‫جزر القمر‬‎)',
            CD: 'Congo (DRC) (Jamhuri ya Kidemokrasia ya Kongo)',
            CG: 'Congo (Republic) (Congo-Brazzaville)',
            CK: 'Cook Islands',
            CR: 'Costa Rica',
            CI: 'Côte d’Ivoire',
            HR: 'Croatia (Hrvatska)',
            CU: 'Cuba',
            CW: 'Curaçao',
            CY: 'Cyprus (Κύπρος)',
            CZ: 'Czech Republic (Česká republika)',
            DK: 'Denmark (Danmark)',
            DJ: 'Djibouti',
            DM: 'Dominica',
            DO: 'Dominican Republic (República Dominicana)',
            EC: 'Ecuador',
            EG: 'Egypt (‫مصر‬‎)',
            SV: 'El Salvador',
            GQ: 'Equatorial Guinea (Guinea Ecuatorial)',
            ER: 'Eritrea',
            EE: 'Estonia (Eesti)',
            ET: 'Ethiopia',
            FK: 'Falkland Islands (Islas Malvinas)',
            FO: 'Faroe Islands (Føroyar)',
            FJ: 'Fiji',
            FI: 'Finland (Suomi)',
            FR: 'France',
            GF: 'French Guiana (Guyane française)',
            PF: 'French Polynesia (Polynésie française)',
            GA: 'Gabon',
            GM: 'Gambia',
            GE: 'Georgia (საქართველო)',
            DE: 'Germany (Deutschland)',
            GH: 'Ghana (Gaana)',
            GI: 'Gibraltar',
            GR: 'Greece (Ελλάδα)',
            GL: 'Greenland (Kalaallit Nunaat)',
            GD: 'Grenada',
            GP: 'Guadeloupe',
            GU: 'Guam',
            GT: 'Guatemala',
            GN: 'Guinea (Guinée)',
            GW: 'Guinea-Bissau (Guiné Bissau)',
            GY: 'Guyana',
            HT: 'Haiti',
            HN: 'Honduras',
            HK: 'Hong Kong (香港)',
            HU: 'Hungary (Magyarország)',
            IS: 'Iceland (Ísland)',
            IN: 'India (भारत)',
            ID: 'Indonesia',
            IR: 'Iran (‫ایران‬‎)',
            IQ: 'Iraq (‫العراق‬‎)',
            IE: 'Ireland',
            IL: 'Israel (‫ישראל‬‎)',
            IT: 'Italy (Italia)',
            JM: 'Jamaica',
            JP: 'Japan (日本)',
            JO: 'Jordan (‫الأردن‬‎)',
            KZ: 'Kazakhstan (Казахстан)',
            KE: 'Kenya',
            KI: 'Kiribati',
            KW: 'Kuwait (‫الكويت‬‎)',
            KG: 'Kyrgyzstan (Кыргызстан)',
            LA: 'Laos (ລາວ)',
            LV: 'Latvia (Latvija)',
            LB: 'Lebanon (‫لبنان‬‎)',
            LS: 'Lesotho',
            LR: 'Liberia',
            LY: 'Libya (‫ليبيا‬‎)',
            LI: 'Liechtenstein',
            LT: 'Lithuania (Lietuva)',
            LU: 'Luxembourg',
            MO: 'Macau (澳門)',
            MK: 'Macedonia (FYROM) (Македонија)',
            MG: 'Madagascar (Madagasikara)',
            MW: 'Malawi',
            MY: 'Malaysia',
            MV: 'Maldives',
            ML: 'Mali',
            MT: 'Malta',
            MH: 'Marshall Islands',
            MQ: 'Martinique',
            MR: 'Mauritania (‫موريتانيا‬‎)',
            MU: 'Mauritius (Moris)',
            MX: 'Mexico (México)',
            FM: 'Micronesia',
            MD: 'Moldova (Republica Moldova)',
            MC: 'Monaco',
            MN: 'Mongolia (Монгол)',
            ME: 'Montenegro (Crna Gora)',
            MS: 'Montserrat',
            MA: 'Morocco (‫المغرب‬‎)',
            MZ: 'Mozambique (Moçambique)',
            MM: 'Myanmar (Burma) (မြန်မာ)',
            NA: 'Namibia (Namibië)',
            NR: 'Nauru',
            NP: 'Nepal (नेपाल)',
            NL: 'Netherlands (Nederland)',
            NC: 'New Caledonia (Nouvelle-Calédonie)',
            NZ: 'New Zealand',
            NI: 'Nicaragua',
            NE: 'Niger (Nijar)',
            NG: 'Nigeria',
            NU: 'Niue',
            NF: 'Norfolk Island',
            KP: 'North Korea (조선 민주주의 인민 공화국)',
            MP: 'Northern Mariana Islands',
            NO: 'Norway (Norge)',
            OM: 'Oman (‫عُمان‬‎)',
            PK: 'Pakistan (‫پاکستان‬‎)',
            PW: 'Palau',
            PS: 'Palestine (‫فلسطين‬‎)',
            PA: 'Panama (Panamá)',
            PG: 'Papua New Guinea',
            PY: 'Paraguay',
            PE: 'Peru (Perú)',
            PH: 'Philippines',
            PL: 'Poland (Polska)',
            PT: 'Portugal',
            PR: 'Puerto Rico',
            QA: 'Qatar (‫قطر‬‎)',
            RE: 'Réunion (La Réunion)',
            RO: 'Romania (România)',
            RU: 'Russia (Россия)',
            RW: 'Rwanda',
            BL: 'Saint Barthélemy (Saint-Barthélemy)',
            SH: 'Saint Helena',
            KN: 'Saint Kitts and Nevis',
            LC: 'Saint Lucia',
            MF: 'Saint Martin (Saint-Martin (partie française))',
            PM: 'Saint Pierre and Miquelon (Saint-Pierre-et-Miquelon)',
            VC: 'Saint Vincent and the Grenadines',
            WS: 'Samoa',
            SM: 'San Marino',
            ST: 'São Tomé and Príncipe (São Tomé e Príncipe)',
            SA: 'Saudi Arabia (‫المملكة العربية السعودية‬‎)',
            SN: 'Senegal (Sénégal)',
            RS: 'Serbia (Србија)',
            SC: 'Seychelles',
            SL: 'Sierra Leone',
            SG: 'Singapore',
            SX: 'Sint Maarten',
            SK: 'Slovakia (Slovensko)',
            SI: 'Slovenia (Slovenija)',
            SB: 'Solomon Islands',
            SO: 'Somalia (Soomaaliya)',
            ZA: 'South Africa',
            KR: 'South Korea (대한민국)',
            SS: 'South Sudan (‫جنوب السودان‬‎)',
            ES: 'Spain (España)',
            LK: 'Sri Lanka (ශ්‍රී ලංකාව)',
            SD: 'Sudan (‫السودان‬‎)',
            SR: 'Suriname',
            SZ: 'Swaziland',
            SE: 'Sweden (Sverige)',
            CH: 'Switzerland (Schweiz)',
            SY: 'Syria (‫سوريا‬‎)',
            TW: 'Taiwan (台灣)',
            TJ: 'Tajikistan',
            TZ: 'Tanzania',
            TH: 'Thailand (ไทย)',
            TL: 'Timor-Leste',
            TG: 'Togo',
            TK: 'Tokelau',
            TO: 'Tonga',
            TT: 'Trinidad and Tobago',
            TN: 'Tunisia (‫تونس‬‎)',
            TR: 'Turkey (Türkiye)',
            TM: 'Turkmenistan',
            TC: 'Turks and Caicos Islands',
            TV: 'Tuvalu',
            VI: 'U.S. Virgin Islands',
            UG: 'Uganda',
            UA: 'Ukraine (Україна)',
            AE: 'United Arab Emirates (‫الإمارات العربية المتحدة‬‎)',
            GB: 'United Kingdom',
            US: 'United States',
            UY: 'Uruguay',
            UZ: 'Uzbekistan (Oʻzbekiston)',
            VU: 'Vanuatu',
            VA: 'Vatican City (Città del Vaticano)',
            VE: 'Venezuela',
            VN: 'Vietnam (Việt Nam)',
            WF: 'Wallis and Futuna',
            YE: 'Yemen (‫اليمن‬‎)',
            ZM: 'Zambia',
            ZW: 'Zimbabwe'
        };
    }

    export function getPhonePrefixByCode() {
        return {
            AF: '+93',
            AL: '+355',
            DZ: '+213',
            AS: '+1684',
            AD: '+376',
            AO: '+244',
            AI: '+1264',
            AG: '+1268',
            AR: '+54',
            AM: '+374',
            AW: '+297',
            AU: '+61',
            AT: '+43',
            AZ: '+994',
            BS: '+1242',
            BH: '+973',
            BD: '+880',
            BB: '+1246',
            BY: '+375',
            BE: '+32',
            BZ: '+501',
            BJ: '+229',
            BM: '+1441',
            BT: '+975',
            BO: '+591',
            BA: '+387',
            BW: '+267',
            BR: '+55',
            IO: '+246',
            VG: '+1284',
            BN: '+673',
            BG: '+359',
            BF: '+226',
            BI: '+257',
            KH: '+855',
            CM: '+237',
            CA: '+1',
            CV: '+238',
            BQ: '+599',
            KY: '+1345',
            CF: '+236',
            TD: '+235',
            CL: '+56',
            CN: '+86',
            CO: '+57',
            KM: '+269',
            CD: '+243',
            CG: '+242',
            CK: '+682',
            CR: '+506',
            CI: '+225',
            HR: '+385',
            CU: '+53',
            CW: '+599',
            CY: '+357',
            CZ: '+420',
            DK: '+45',
            DJ: '+253',
            DM: '+1767',
            DO: '+1',
            EC: '+593',
            EG: '+20',
            SV: '+503',
            GQ: '+240',
            ER: '+291',
            EE: '+372',
            ET: '+251',
            FK: '+500',
            FO: '+298',
            FJ: '+679',
            FI: '+358',
            FR: '+33',
            GF: '+594',
            PF: '+689',
            GA: '+241',
            GM: '+220',
            GE: '+995',
            DE: '+49',
            GH: '+233',
            GI: '+350',
            GR: '+30',
            GL: '+299',
            GD: '+1473',
            GP: '+590',
            GU: '+1671',
            GT: '+502',
            GN: '+224',
            GW: '+245',
            GY: '+592',
            HT: '+509',
            HN: '+504',
            HK: '+852',
            HU: '+36',
            IS: '+354',
            IN: '+91',
            ID: '+62',
            IR: '+98',
            IQ: '+964',
            IE: '+353',
            IL: '+972',
            IT: '+39',
            JM: '+1876',
            JP: '+81',
            JO: '+962',
            KZ: '+7',
            KE: '+254',
            KI: '+686',
            KW: '+965',
            KG: '+996',
            LA: '+856',
            LV: '+371',
            LB: '+961',
            LS: '+266',
            LR: '+231',
            LY: '+218',
            LI: '+423',
            LT: '+370',
            LU: '+352',
            MO: '+853',
            MK: '+389',
            MG: '+261',
            MW: '+265',
            MY: '+60',
            MV: '+960',
            ML: '+223',
            MT: '+356',
            MH: '+692',
            MQ: '+596',
            MR: '+222',
            MU: '+230',
            MX: '+52',
            FM: '+691',
            MD: '+373',
            MC: '+377',
            MN: '+976',
            ME: '+382',
            MS: '+1664',
            MA: '+212',
            MZ: '+258',
            MM: '+95',
            NA: '+264',
            NR: '+674',
            NP: '+977',
            NL: '+31',
            NC: '+687',
            NZ: '+64',
            NI: '+505',
            NE: '+227',
            NG: '+234',
            NU: '+683',
            NF: '+672',
            KP: '+850',
            MP: '+1670',
            NO: '+47',
            OM: '+968',
            PK: '+92',
            PW: '+680',
            PS: '+970',
            PA: '+507',
            PG: '+675',
            PY: '+595',
            PE: '+51',
            PH: '+63',
            PL: '+48',
            PT: '+351',
            PR: '+1',
            QA: '+974',
            RE: '+262',
            RO: '+40',
            RU: '+7',
            RW: '+250',
            BL: '+590',
            SH: '+290',
            KN: '+1869',
            LC: '+1758',
            MF: '+590',
            PM: '+508',
            VC: '+1784',
            WS: '+685',
            SM: '+378',
            ST: '+239',
            SA: '+966',
            SN: '+221',
            RS: '+381',
            SC: '+248',
            SL: '+232',
            SG: '+65',
            SX: '+1721',
            SK: '+421',
            SI: '+386',
            SB: '+677',
            SO: '+252',
            ZA: '+27',
            KR: '+82',
            SS: '+211',
            ES: '+34',
            LK: '+94',
            SD: '+249',
            SR: '+597',
            SZ: '+268',
            SE: '+46',
            CH: '+41',
            SY: '+963',
            TW: '+886',
            TJ: '+992',
            TZ: '+255',
            TH: '+66',
            TL: '+670',
            TG: '+228',
            TK: '+690',
            TO: '+676',
            TT: '+1868',
            TN: '+216',
            TR: '+90',
            TM: '+993',
            TC: '+1649',
            TV: '+688',
            VI: '+1340',
            UG: '+256',
            UA: '+380',
            AE: '+971',
            GB: '+44',
            US: '+1',
            UY: '+598',
            UZ: '+998',
            VU: '+678',
            VA: '+39',
            VE: '+58',
            VN: '+84',
            WF: '+681',
            YE: '+967',
            ZM: '+260',
            ZW: '+263'
        };
    }

    export function getNearestCountryCodeByLanguage() {
        return {
            de: 'DE',
            en: 'US',
            es: 'ES',
            fi: 'FI',
            fr: 'FR',
            it: 'IT',
            ja: 'JP',
            nl: 'NL',
            sv: 'SE'
        };
    }
}
